import React, { useState, useEffect, useCallback } from 'react';
import { Button, Modal, Toast } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import { fetchWithCSRF } from '../utils/csrfUtils';
import { getTodayLocalDateString } from '../utils/dateUtils';
import { getIngredientImageUrl } from '../utils/imageUtils';
import { useUpdatePantryIngredient, useAddPantryIngredient } from '../hooks/pantryIngredient';
import ErrorBoundary from '../components/ErrorBoundary';
import GroceryDrawer from '../components/GroceryDrawer';
import RecipesForIngredientDrawer from '../components/RecipesForIngredientDrawer';
import '../index.css';

const PantryAndGroceries = () => {
    const { pgUser, sessionToken } = useAuth();
    const { updatePantryIngredient } = useUpdatePantryIngredient();
    const { addPantryIngredient } = useAddPantryIngredient();
    
    // State declarations
    const [isAuthLoading, setIsAuthLoading] = useState(true);
    const [pantryId, setPantryId] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentGroceryList, setCurrentGroceryList] = useState(null);
    const [pantryIngredients, setPantryIngredients] = useState([]);
    const [combinedData, setCombinedData] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [editingQuantityId, setEditingQuantityId] = useState(null);
    const [editingQuantity, setEditingQuantity] = useState('');
    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [toastType, setToastType] = useState('success'); // 'success' or 'error'
    const [initialCategories, setInitialCategories] = useState(null); // Store initial categorization
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [isRecipeDrawerOpen, setIsRecipeDrawerOpen] = useState(false);
    const [selectedIngredientForRecipes, setSelectedIngredientForRecipes] = useState(null);

    // Initialize pgUser-dependent state
    useEffect(() => {
        if (!pgUser) {
            setIsAuthLoading(false);
            return;
        }
        setPantryId(pgUser?.pantry_id || null);
        setIsAuthLoading(false);
    }, [pgUser]);

    // Helper function to format quantity display
    const formatQuantity = (quantity) => {
        const num = Number(quantity);
        if (Number.isInteger(num)) {
            return num.toString();
        }
        return num.toFixed(2).replace(/\.?0+$/, '');
    };

    // Helper function to show toast notifications
    const showToastNotification = (message, type = 'success') => {
        setToastMessage(message);
        setToastType(type);
        setShowToast(true);
        setTimeout(() => setShowToast(false), 3000);
    };

    // Fetch current grocery list
    const fetchCurrentGroceryList = useCallback(async () => {
        if (!pgUser?.id || !sessionToken) return null;

        try {
            // Get today's date in YYYY-MM-DD format
            const today = getTodayLocalDateString();
            
            const response = await fetchWithCSRF(`/api/v1/grocery-list/current/${pgUser.id}/date/${today}`, {
                headers: { 'Authorization': `Bearer ${sessionToken}` }
            });

            if (!response.ok) {
                throw new Error('Failed to fetch current grocery list');
            }

            const data = await response.json();
            console.log('[PantryAndGroceries.js] Raw grocery list response:', data);
            // The response is an array with one item containing the data object
            if (data && data.length > 0 && data[0].data) {
                console.log('[PantryAndGroceries.js] Grocery list data:', data[0].data);
                console.log('[PantryAndGroceries.js] Grocery list ingredients:', data[0].data.grocery_list_ingredients);
                return data[0].data;
            }
            console.log('[PantryAndGroceries.js] No grocery list data found');
            return null;
        } catch (err) {
            console.error('Error fetching current grocery list:', err);
            return null;
        }
    }, [pgUser?.id, sessionToken]);

    // Fetch pantry ingredients
    const fetchPantryIngredients = useCallback(async () => {
        if (!pantryId || !pgUser?.id || !sessionToken) return [];

        try {
            const response = await fetchWithCSRF(`/api/v1/pantry-ingredient/pantry/${pantryId}/app_user/${pgUser.id}`, {
                headers: { 'Authorization': `Bearer ${sessionToken}` }
            });

            if (!response.ok) {
                throw new Error('Failed to fetch pantry ingredients');
            }

            const data = await response.json();
            return Array.isArray(data) ? data : [];
        } catch (err) {
            console.error('Error fetching pantry ingredients:', err);
            return [];
        }
    }, [pantryId, pgUser?.id, sessionToken]);

    // Combine pantry and grocery list data
    const combineData = useCallback((pantryItems, groceryListData) => {
        console.log('[PantryAndGroceries.js] === COMBINE DATA DEBUG ===');
        console.log('[PantryAndGroceries.js] Pantry items count:', pantryItems?.length || 0);
        console.log('[PantryAndGroceries.js] Grocery list data:', groceryListData);
        
        const groceryIngredients = groceryListData?.grocery_list_ingredients || [];
        console.log('[PantryAndGroceries.js] Grocery ingredients count:', groceryIngredients.length);
        console.log('[PantryAndGroceries.js] First grocery ingredient:', groceryIngredients[0]);
        
        // Create a map of grocery list ingredients by ingredient_id
        const groceryMap = new Map();
        groceryIngredients.forEach(item => {
            groceryMap.set(item.ingredient_id, {
                quantity: item.quantity,
                unit_of_measurement: item.unit_of_measurement,
                ingredient_name: item.ingredient_name,
                primary_food_group: item.primary_food_group
            });
        });
        console.log('[PantryAndGroceries.js] Grocery map size:', groceryMap.size);

        // Create a map of pantry ingredients by ingredient_id
        const pantryMap = new Map();
        pantryItems.forEach(item => {
            const existing = pantryMap.get(item.ingredient_id);
            if (existing) {
                // If ingredient exists in pantry multiple times, sum the quantities
                pantryMap.set(item.ingredient_id, {
                    ...existing,
                    quantity: existing.quantity + parseFloat(item.quantity),
                    // Keep the first pantry ingredient id for editing purposes
                    pantry_ingredient_ids: [...existing.pantry_ingredient_ids, item.id]
                });
            } else {
                pantryMap.set(item.ingredient_id, {
                    ingredient_id: item.ingredient_id,
                    ingredient_name: item.ingredient_name,
                    quantity: parseFloat(item.quantity),
                    unit_of_measurement: item.unit_of_measurement,
                    primary_food_group: item.primary_food_group,
                    pantry_ingredient_ids: [item.id]
                });
            }
        });

        // Combine the data
        const combined = [];
        
        // Get all unique ingredient IDs from both pantry and grocery list
        const allIngredientIds = new Set([
            ...pantryMap.keys(),
            ...groceryMap.keys()
        ]);
        console.log('[PantryAndGroceries.js] All unique ingredient IDs count:', allIngredientIds.size);
        console.log('[PantryAndGroceries.js] Pantry map keys:', Array.from(pantryMap.keys()));
        console.log('[PantryAndGroceries.js] Grocery map keys:', Array.from(groceryMap.keys()));
        console.log('[PantryAndGroceries.js] Grocery map keys:', Array.from(groceryMap.keys()));

        allIngredientIds.forEach(ingredientId => {
            const pantryItem = pantryMap.get(ingredientId);
            const groceryItem = groceryMap.get(ingredientId);
            
            const pantryQty = pantryItem?.quantity || 0;
            const groceryQty = groceryItem?.quantity || 0;
            const additionalNeeded = Math.max(0, groceryQty - pantryQty);
            
            combined.push({
                ingredient_id: ingredientId,
                ingredient_name: pantryItem?.ingredient_name || groceryItem?.ingredient_name,
                pantry_quantity: pantryQty,
                grocery_quantity: groceryQty,
                additional_needed: additionalNeeded,
                unit_of_measurement: pantryItem?.unit_of_measurement || groceryItem?.unit_of_measurement,
                primary_food_group: pantryItem?.primary_food_group || groceryItem?.primary_food_group || 'Other',
                pantry_ingredient_ids: pantryItem?.pantry_ingredient_ids || [],
                is_needed_for_grocery: groceryQty > 0
            });
        });

        // Sort: ingredients needed for grocery list first, then by food group, then by name
        combined.sort((a, b) => {
            // First sort by whether it's needed for grocery list
            if (a.is_needed_for_grocery !== b.is_needed_for_grocery) {
                return b.is_needed_for_grocery ? 1 : -1;
            }
            // Then by food group
            if (a.primary_food_group !== b.primary_food_group) {
                return a.primary_food_group.localeCompare(b.primary_food_group);
            }
            // Finally by ingredient name
            return a.ingredient_name.localeCompare(b.ingredient_name);
        });

        console.log('[PantryAndGroceries.js] Combined data count:', combined.length);
        console.log('[PantryAndGroceries.js] First 3 combined items:', combined.slice(0, 3));
        console.log('[PantryAndGroceries.js] === END COMBINE DATA DEBUG ===');
        
        return combined;
    }, []);

    // Categorize ingredients into two sections
    const categorizeIngredients = useCallback((data) => {
        const neededThisWeek = [];
        const notUsedThisWeek = [];

        data.forEach(item => {
            if (item.grocery_quantity > 0) {
                // Items needed for recipes this week
                neededThisWeek.push(item);
            } else {
                // Items not needed for recipes this week
                notUsedThisWeek.push(item);
            }
        });

        return { neededThisWeek, notUsedThisWeek };
    }, []);

    // Fetch all data
    useEffect(() => {
        const fetchAllData = async () => {
            if (!pantryId || !pgUser?.id) return;

            setIsLoading(true);
            setError(null);

            try {
                const [groceryData, pantryData] = await Promise.all([
                    fetchCurrentGroceryList(),
                    fetchPantryIngredients()
                ]);

                setCurrentGroceryList(groceryData);
                setPantryIngredients(pantryData);

                const combined = combineData(pantryData, groceryData);
                setCombinedData(combined);

                // Store initial categorization (only on first load)
                const categorized = categorizeIngredients(combined);
                setInitialCategories(categorized);
            } catch (err) {
                console.error('Error fetching data:', err);
                setError('Failed to load data. Please refresh and try again.');
            } finally {
                setIsLoading(false);
            }
        };

        fetchAllData();
    }, [pantryId, pgUser?.id, fetchCurrentGroceryList, fetchPantryIngredients, combineData, categorizeIngredients]);

    // Get current data with updated quantities from combinedData
    // But keep the initial category assignments (items stay in their original sections)
    const getCategorizedDataWithUpdates = () => {
        if (!initialCategories) {
            return { neededThisWeek: [], notUsedThisWeek: [] };
        }

        // Create a map of current data by ingredient_id for quick lookup
        const currentDataMap = new Map();
        combinedData.forEach(item => {
            currentDataMap.set(item.ingredient_id, item);
        });

        // Filter by search query and update with current quantities
        const filterAndUpdate = (items) => {
            return items
                .map(item => currentDataMap.get(item.ingredient_id) || item)
                .filter(item => 
                    item?.ingredient_name?.toLowerCase().includes(searchQuery.toLowerCase()) ?? false
                );
        };

        return {
            neededThisWeek: filterAndUpdate(initialCategories.neededThisWeek),
            notUsedThisWeek: filterAndUpdate(initialCategories.notUsedThisWeek)
        };
    };

    const { neededThisWeek, notUsedThisWeek } = getCategorizedDataWithUpdates();

    // Group each category by food group
    const groupByFoodGroup = (items) => {
        return items.reduce((acc, item) => {
            const group = item.primary_food_group || 'Other';
            if (!acc[group]) acc[group] = [];
            acc[group].push(item);
            return acc;
        }, {});
    };

    const neededGrouped = groupByFoodGroup(neededThisWeek);
    const notUsedGrouped = groupByFoodGroup(notUsedThisWeek);

    // Handle "I Have Enough" button click
    const handleIHaveEnough = async (item) => {
        // Set pantry quantity to match grocery quantity
        await updateQuantity(item, item.grocery_quantity);
    };

    // Render ingredient item (reusable component)
    const renderIngredientItem = (item, borderColor = 'transparent', showIHaveEnough = false) => (
        <div
            key={item.ingredient_id}
            className="d-flex justify-content-between align-items-center px-1 py-1"
            style={{
                // border: borderColor !== 'transparent' ? `1px solid ${borderColor}` : 'none',
                // borderRadius: borderColor !== 'transparent' ? '4px' : '0px',
                borderBottom: '1px solid #e0e0e0',
                marginBottom: '4px'
            }}
        >
            {/* Column 1: Ingredient Name */}
            <div style={{ width: '40%', textAlign: 'left' }} className="d-flex align-items-start flex-column">
                <div className="d-flex align-items-center">
                    <img
                        src={getIngredientImageUrl(item.ingredient_id)}
                        alt={item.ingredient_name}
                        style={{
                            width: '1.25rem',
                            height: '1.25rem',
                            borderRadius: '4px',
                            marginRight: '0.5rem',
                            objectFit: 'cover'
                        }}
                        onError={e => {
                            e.target.onerror = null;
                            e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIGZpbGw9IiNFMEUwRTAiLz48L3N2Zz4=';
                        }}
                    />
                    <span style={{ fontWeight: '500', fontSize: '0.9rem', textAlign: 'left' }}>
                        {item.ingredient_name}
                    </span>
                </div>
                {showIHaveEnough && item.additional_needed > 0 ? (
                    <button
                        onClick={() => handleIHaveEnough(item)}
                        style={{ 
                            fontSize: '0.75rem', 
                            color: 'white',
                            backgroundColor: '#52cd92',
                            border: '1px solid #52cd92',
                            borderRadius: '12px',
                            cursor: 'pointer',
                            marginLeft: '1.75rem',
                            padding: '4px 10px',
                            fontWeight: '500',
                            transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                            e.target.style.backgroundColor = '#45b880';
                            e.target.style.borderColor = '#45b880';
                        }}
                        onMouseLeave={(e) => {
                            e.target.style.backgroundColor = '#52cd92';
                            e.target.style.borderColor = '#52cd92';
                        }}
                    >
                        I Have Enough
                    </button>
                ) : !showIHaveEnough ? (
                    <span 
                        onClick={() => {
                            setSelectedIngredientForRecipes({
                                id: item.ingredient_id,
                                name: item.ingredient_name
                            });
                            setIsRecipeDrawerOpen(true);
                        }}
                        style={{ 
                            fontSize: '0.75rem', 
                            color: '#52cd92', 
                            cursor: 'pointer',
                            marginLeft: '1.75rem',
                            textDecoration: 'none',
                            fontWeight: '500'
                        }}
                        onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                        onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                    >
                        See Recipes
                    </span>
                ) : null}
            </div>

            {/* Column 2: Pantry Quantity (Editable) */}
            <div style={{ width: '40%', textAlign: 'left' }}>
                {editingQuantityId === item.ingredient_id ? (
                    <div className="d-flex flex-column align-items-start justify-content-start">
                        <input
                            type="number"
                            className="form-control form-control-sm"
                            value={editingQuantity}
                            onChange={(e) => handleQuantityInputChange(e.target.value)}
                            onBlur={() => handleQuantitySubmit(item)}
                            onKeyDown={(e) => handleQuantityKeyPress(e, item)}
                            autoFocus
                            min="0"
                            step="0.25"
                            style={{ width: '80px', textAlign: 'center' }}
                        />
                        <span className="text-muted small mt-1" style={{ fontSize: '0.75rem', width: '80px', textAlign: 'center', display: 'block' }}>
                            {item.unit_of_measurement}
                        </span>
                    </div>
                ) : (
                    <div className="d-flex flex-column align-items-start justify-content-start">
                        <div className="d-flex align-items-center justify-content-start gap-0">
                            <button
                                className="btn btn-sm btn-outline-secondary"
                                onClick={() => handleQuantityChange(item, -0.25)}
                                disabled={item.pantry_quantity <= 0}
                                style={{ width: '30px', height: '30px', padding: '0' }}
                            >
                                −
                            </button>
                            <span
                                onClick={() => handleQuantityEdit(item)}
                                style={{
                                    cursor: 'pointer',
                                    minWidth: '60px',
                                    textAlign: 'center',
                                    padding: '4px',
                                    borderRadius: '4px',
                                    fontWeight: '500'
                                }}
                                title="Click to edit"
                            >
                                {formatQuantity(item.pantry_quantity)}
                            </span>
                            <button
                                className="btn btn-sm btn-outline-secondary"
                                onClick={() => handleQuantityChange(item, 0.25)}
                                style={{ width: '30px', height: '30px', padding: '0' }}
                            >
                                +
                            </button>
                        </div>
                        <span className="text-muted small mt-1" style={{ fontSize: '0.75rem', width: '120px', textAlign: 'center', display: 'block' }}>
                            {item.unit_of_measurement}
                        </span>
                    </div>
                )}
            </div>

            {/* Column 3: Additional Needed */}
            <div style={{ width: '20%', textAlign: 'center' }}>
                <div className="d-flex flex-column align-items-center justify-content-center">
                    <span style={{ 
                        fontWeight: '500',
                        color: item.additional_needed > 0 ? '#d9534f' : '#5cb85c'
                    }}>
                        {formatQuantity(item.additional_needed)}
                    </span>
                    <span className="text-muted small mt-1" style={{ fontSize: '0.75rem' }}>
                        {item.unit_of_measurement}
                    </span>
                </div>
            </div>
        </div>
    );

    // Handle quantity edit
    const handleQuantityEdit = (item) => {
        setEditingQuantityId(item.ingredient_id);
        setEditingQuantity(formatQuantity(item.pantry_quantity));
    };

    // Handle quantity change with +/- buttons
    const handleQuantityChange = async (item, delta) => {
        const newQuantity = Math.max(0, item.pantry_quantity + delta);
        await updateQuantity(item, newQuantity);
    };

    // Handle manual quantity input
    const handleQuantityInputChange = (value) => {
        setEditingQuantity(value);
    };

    // Update quantity in database
    const updateQuantity = async (item, newQuantity) => {
        try {
            // Check if ingredient exists in pantry
            if (item.pantry_ingredient_ids.length === 0) {
                // Ingredient doesn't exist in pantry yet - create it
                console.log('Creating new pantry ingredient:', {
                    pantry_id: pantryId,
                    ingredient_id: item.ingredient_id,
                    unit_of_measurement: item.unit_of_measurement,
                    quantity: Number(newQuantity)
                });

                const result = await addPantryIngredient({
                    pantry_id: pantryId,
                    ingredient_id: item.ingredient_id,
                    unit_of_measurement: item.unit_of_measurement,
                    quantity: Number(newQuantity),
                    is_confirmed: true,
                    confirmed_at: new Date().toISOString()
                });

                if (result && result.data) {
                    // Update local state with the new pantry ingredient
                    setCombinedData(prev => prev.map(i => 
                        i.ingredient_id === item.ingredient_id
                            ? { 
                                ...i, 
                                pantry_quantity: Number(newQuantity),
                                additional_needed: Math.max(0, i.grocery_quantity - Number(newQuantity)),
                                pantry_ingredient_ids: [result.data.id]
                              }
                            : i
                    ));
                    showToastNotification('Added to pantry');
                } else {
                    showToastNotification('Failed to add to pantry', 'error');
                }
            } else {
                // Ingredient exists - update it
                const result = await updatePantryIngredient(
                    item.pantry_ingredient_ids[0],
                    pantryId,
                    pgUser.id,
                    { quantity: Number(newQuantity) }
                );

                if (result) {
                    // Update local state
                    setCombinedData(prev => prev.map(i => 
                        i.ingredient_id === item.ingredient_id
                            ? { 
                                ...i, 
                                pantry_quantity: Number(newQuantity),
                                additional_needed: Math.max(0, i.grocery_quantity - Number(newQuantity))
                              }
                            : i
                    ));
                    showToastNotification('Pantry quantity updated');
                } else {
                    showToastNotification('Failed to update quantity', 'error');
                }
            }
        } catch (error) {
            console.error('Error updating/adding quantity:', error);
            showToastNotification('Error updating quantity', 'error');
        }
    };

    // Handle quantity submit (when user finishes editing)
    const handleQuantitySubmit = async (item) => {
        const newQuantity = parseFloat(editingQuantity);
        if (isNaN(newQuantity) || newQuantity < 0) {
            showToastNotification('Please enter a valid quantity', 'error');
            setEditingQuantityId(null);
            return;
        }

        await updateQuantity(item, newQuantity);
        setEditingQuantityId(null);
    };

    // Handle key press in quantity input
    const handleQuantityKeyPress = (e, item) => {
        if (e.key === 'Enter') {
            handleQuantitySubmit(item);
        } else if (e.key === 'Escape') {
            setEditingQuantityId(null);
        }
    };

    // Format date range for display
    const formatDateRange = (startDate, endDate) => {
        if (!startDate || !endDate) return '';
        
        const start = new Date(startDate);
        const end = new Date(endDate);
        
        const formatDate = (date) => {
            const month = date.toLocaleString('en-US', { month: 'short' });
            const day = date.getDate();
            return `${month} ${day}`;
        };
        
        return `${formatDate(start)} - ${formatDate(end)}`;
    };

    // Handle items added to pantry from drawer
    const handleItemsAddedToPantry = useCallback(async () => {
        // Refetch pantry and grocery list data
        const [groceryData, pantryData] = await Promise.all([
            fetchCurrentGroceryList(),
            fetchPantryIngredients()
        ]);

        setCurrentGroceryList(groceryData);
        setPantryIngredients(pantryData);

        const combined = combineData(pantryData, groceryData);
        setCombinedData(combined);
    }, [fetchCurrentGroceryList, fetchPantryIngredients, combineData]);

    return (
        <ErrorBoundary fallbackMessage="Unable to load pantry and groceries. Please try again later.">
            <div className="container">
                {isAuthLoading ? (
                    <div className="text-center my-4">
                        <div className="spinner-border" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                ) : !pgUser ? (
                    <div className="alert alert-warning">
                        <p className="h5 mb-3">Unable to connect to the server. Please try again later or send a message to <a href="mailto:support@getautochef.com">support@getautochef.com</a></p>
                        <Button onClick={() => window.location.reload()}>Retry</Button>
                    </div>
                ) : (
                    <>
                        <div className="row mb-3 mt-4">
                            <div className="col text-center">
                                {/* Search Bar */}
                                <div className="position-relative" style={{ maxWidth: '400px', margin: '0 auto' }}>
                                    <svg 
                                        width="16" 
                                        height="16" 
                                        viewBox="0 0 16 16" 
                                        fill="currentColor"
                                        style={{ 
                                            position: 'absolute', 
                                            left: '12px', 
                                            top: '37%', 
                                            transform: 'translateY(-50%)',
                                            opacity: 0.4,
                                            color: '#999',
                                            pointerEvents: 'none'
                                        }}
                                    >
                                        <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z"/>
                                    </svg>
                                    <input
                                        type="text"
                                        placeholder="Search pantry"
                                        value={searchQuery}
                                        onChange={e => setSearchQuery(e.target.value)}
                                        className="form-control-custom mb-3"
                                        style={{
                                            width: '100%',
                                            fontSize: '0.9rem',
                                            padding: '0.5rem 0.5rem 0.5rem 36px',
                                            backgroundColor: '#fafafa',
                                            border: '1px solid #e0e0e0',
                                            color: '#666'
                                        }}
                                    />
                                </div>

                                {/* Date Range Display */}
                                {/* {currentGroceryList?.grocery_list && (
                                    <div className="d-flex justify-content-center align-items-center mb-3">
                                        <h4 className="mb-0" style={{ fontWeight: 'bold' }}>
                                            {formatDateRange(
                                                currentGroceryList.grocery_list.start_date,
                                                currentGroceryList.grocery_list.end_date
                                            )}
                                        </h4>
                                    </div>
                                )} */}

                                {/* Show My Groceries Button */}
                                <Button
                                    onClick={() => setIsDrawerOpen(true)}
                                    disabled={!currentGroceryList}
                                    style={{
                                        backgroundColor: '#52cd92',
                                        border: 'none',
                                        color: 'white',
                                        padding: '0.5rem 1.5rem',
                                        fontSize: '1rem',
                                        fontWeight: '500'
                                    }}
                                >
                                Show My Grocery List
                                </Button>
                            </div>
                        </div>

                        {isLoading ? (
                            <div className="text-center my-4">
                                <div className="spinner-border" role="status" style={{ color: '#52cd92' }}>
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        ) : error ? (
                            <div className="alert alert-danger" role="alert">
                                {error}
                            </div>
                        ) : (
                            <div className="row justify-content-center">
                                <div className="col-12" style={{ maxWidth: '800px' }}>
                                    {/* Two Category Sections */}
                                    {(neededThisWeek.length === 0 && notUsedThisWeek.length === 0) ? (
                                        <div className="text-center py-5">
                                            <p className="text-muted">No ingredients found</p>
                                        </div>
                                    ) : (
                                        <>
                                            {/* Section 1: Check Your Pantry */}
                                            {neededThisWeek.length > 0 && (
                                                <div className="mb-5">
                                                    <h3 style={{ 
                                                        fontWeight: '700', 
                                                        // marginTop: '1.5rem', 
                                                        marginBottom: '1rem',
                                                        textAlign: 'center',
                                                        color: '#52cd92',
                                                        fontSize: '1.5rem'
                                                    }}>
                                                        Check Your Pantry
                                                    </h3>
                                                    <p style={{ textAlign: 'center', color: '#666', marginBottom: '1rem', fontSize: '0.9rem' }}>
                                                        These ingredients are needed for this week's recipes. Verify what you have and update the quantities.
                                                    </p>
                                                    
                                                    {/* Column Headers for this section */}
                                                    <div className="d-flex justify-content-between align-items-center px-3 py-2 mb-2" 
                                                         style={{ 
                                                             fontWeight: '600', 
                                                             borderBottom: '2px solid #52cd92',
                                                             fontSize: '0.9rem'
                                                         }}>
                                                        <div style={{ width: '40%', textAlign: 'left' }}>Ingredient</div>
                                                        <div style={{ width: '40%', textAlign: 'left' }}>You Already Have</div>
                                                        <div style={{ width: '20%', textAlign: 'left' }}>Additional Needed</div>
                                                    </div>
                                                    
                                                    {Object.keys(neededGrouped).sort().map(foodGroup => (
                                                        <div key={foodGroup} className="mb-3">
                                                            {/* <h4 style={{ 
                                                                fontWeight: '700', 
                                                                marginTop: '1rem', 
                                                                marginBottom: '0.5rem',
                                                                textAlign: 'left',
                                                                paddingLeft: '10px',
                                                                fontSize: '1.1rem'
                                                            }}>
                                                                {foodGroup}
                                                            </h4> */}
                                                            {neededGrouped[foodGroup]
                                                                .sort((a, b) => {
                                                                    // First, sort by pantry quantity (items with qty > 0 first)
                                                                    const aHasQty = a.pantry_quantity > 0 ? 1 : 0;
                                                                    const bHasQty = b.pantry_quantity > 0 ? 1 : 0;
                                                                    if (aHasQty !== bHasQty) {
                                                                        return bHasQty - aHasQty; // Higher value first
                                                                    }
                                                                    // Then sort alphabetically by name
                                                                    return a.ingredient_name.localeCompare(b.ingredient_name);
                                                                })
                                                                .map(item => renderIngredientItem(item, '#52cd92', true))}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}

                                            {/* Section 2: Not Used This Week */}
                                            {notUsedThisWeek.length > 0 && (
                                                <div className="mb-5">
                                                    <h3 style={{ 
                                                        fontWeight: '700', 
                                                        marginTop: '1.5rem', 
                                                        marginBottom: '1rem',
                                                        textAlign: 'center',
                                                        color: '#999',
                                                        fontSize: '1.5rem'
                                                    }}>
                                                        Not Used This Week
                                                    </h3>
                                                    
                                                    {/* Column Headers for this section */}
                                                    <div className="d-flex justify-content-between align-items-center px-3 py-2 mb-2" 
                                                         style={{ 
                                                             fontWeight: '600', 
                                                             borderBottom: '2px solid #999',
                                                             fontSize: '0.9rem',
                                                             color: '#999'
                                                         }}>
                                                        <div style={{ width: '40%', textAlign: 'left' }}>Ingredient</div>
                                                        <div style={{ width: '40%', textAlign: 'left' }}>You Already Have</div>
                                                        <div style={{ width: '20%', textAlign: 'left' }}>Additional Needed</div>
                                                    </div>
                                                    
                                                    {Object.keys(notUsedGrouped).sort().map(foodGroup => (
                                                        <div key={foodGroup} className="mb-3">
                                                            <h4 style={{ 
                                                                fontWeight: '700', 
                                                                marginTop: '1rem', 
                                                                marginBottom: '0.5rem',
                                                                textAlign: 'left',
                                                                paddingLeft: '10px',
                                                                fontSize: '1.1rem',
                                                                color: '#999'
                                                            }}>
                                                                {foodGroup}
                                                            </h4>
                                                            {notUsedGrouped[foodGroup].map(item => renderIngredientItem(item, 'transparent', false))}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                        )}
                    </>
                )}

                {/* Toast Notification */}
                <Toast
                    show={showToast}
                    onClose={() => setShowToast(false)}
                    style={{
                        position: 'fixed',
                        top: '20px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        zIndex: 9999,
                        backgroundColor: toastType === 'success' ? '#52cd92' : '#dc3545',
                        border: 'none',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        minWidth: '300px'
                    }}
                >
                    <Toast.Header
                        style={{
                            backgroundColor: toastType === 'success' ? '#52cd92' : '#dc3545',
                            borderBottom: '1px solid rgba(255,255,255,0.2)',
                            justifyContent: 'center'
                        }}
                    >
                        <strong style={{ color: 'white' }}>{toastMessage}</strong>
                    </Toast.Header>
                </Toast>

                {/* Grocery Drawer */}
                <GroceryDrawer
                    isOpen={isDrawerOpen}
                    onClose={() => setIsDrawerOpen(false)}
                    groceryListId={currentGroceryList?.grocery_list?.id}
                    onItemsAddedToPantry={handleItemsAddedToPantry}
                />

                {/* Recipes For Ingredient Drawer */}
                <RecipesForIngredientDrawer
                    isOpen={isRecipeDrawerOpen}
                    onClose={() => setIsRecipeDrawerOpen(false)}
                    ingredient={selectedIngredientForRecipes}
                />
            </div>
        </ErrorBoundary>
    );
};

export default PantryAndGroceries;

